import React, { useEffect, useState } from 'react';
import { Spin, Button, Space, Tooltip } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, CompressOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import rbacService from '../../services/rbacService';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;
const ZOOM_DEFAULT = 1;

const PORTAL_ROLE_NAMES = new Set(['partner', 'investor']);

function isPortalRole(roleName) {
  return roleName != null && PORTAL_ROLE_NAMES.has(String(roleName).trim().toLowerCase());
}

function internalDisplayRoles(roles) {
  return (Array.isArray(roles) ? roles : []).filter((role) => !isPortalRole(role));
}

/* ─────────────────────────────────────────────────────────────
   STEP 1 · DATA NORMALISATION
   Raw API array  →  single internal root tree
   The API gives depth-1 direct reports; we synthesise the
   logged-in user as the virtual root.
───────────────────────────────────────────────────────────── */
function normaliseNode(raw) {
  const roles = internalDisplayRoles(raw.roles);
  return {
    id:          raw.member_id,
    name:        raw.name        ?? '',
    email:       raw.email       ?? '',
    primaryRole: roles[0]        ?? '',
    extraRoles:  Math.max(0, roles.length - 1),
    entityType:  raw.entity_type ?? null,
    level:       raw.level       ?? null,
    isRoot:      false,
    children:    (raw.children ?? []).map(normaliseNode),
  };
}

function normaliseOrgData(apiData, authUser) {
  const authRoles = internalDisplayRoles(authUser?.roles);
  return {
    id:          'root',
    name:        authUser?.name  ?? 'You',
    email:       authUser?.email ?? '',
    primaryRole: authRoles[0]    ?? '',
    extraRoles:  Math.max(0, authRoles.length - 1),
    entityType:  authUser?.entity_type ?? null,
    level:       null,
    isRoot:      true,
    children:    (apiData ?? []).map(normaliseNode),
  };
}

/* ─────────────────────────────────────────────────────────────
   STEP 2 · VISUAL HELPERS
───────────────────────────────────────────────────────────── */
const LINE   = '#c7d2fe';   // indigo-200
const LINE_W = 2;           // px
const V_H    = 30;          // vertical connector height (px)
const H_GAP  = 20;          // horizontal gap between sibling columns (px)

function levelTheme(level, isRoot) {
  if (isRoot)      return { bar: 'linear-gradient(90deg,#6366f1,#8b5cf6)', av: '#ede9fe', tx: '#5b21b6' };
  if (!level)      return { bar: '#94a3b8',                                 av: '#f1f5f9', tx: '#475569' };
  if (level <= 5)  return { bar: 'linear-gradient(90deg,#6366f1,#8b5cf6)', av: '#ede9fe', tx: '#5b21b6' };
  if (level <= 15) return { bar: 'linear-gradient(90deg,#0ea5e9,#38bdf8)', av: '#e0f2fe', tx: '#0369a1' };
  if (level <= 25) return { bar: 'linear-gradient(90deg,#10b981,#34d399)', av: '#d1fae5', tx: '#065f46' };
  if (level <= 35) return { bar: 'linear-gradient(90deg,#f59e0b,#fbbf24)', av: '#fef3c7', tx: '#92400e' };
  return               { bar: 'linear-gradient(90deg,#64748b,#94a3b8)', av: '#f1f5f9', tx: '#334155' };
}

function initials(name) {
  return (name || '?').trim().split(/\s+/).map(w => w[0] ?? '').join('').slice(0, 2).toUpperCase();
}

/* ─────────────────────────────────────────────────────────────
   STEP 3 · CARD
───────────────────────────────────────────────────────────── */
function OrgCard({ node }) {
  const t = levelTheme(node.level, node.isRoot);
  return (
    <div style={{
      width: 210,
      background: '#fff',
      border: node.isRoot ? `2px solid #6366f1` : '1px solid #e2e8f0',
      borderRadius: 14,
      overflow: 'hidden',
      boxSizing: 'border-box',
      boxShadow: node.isRoot
        ? '0 4px 24px rgba(99,102,241,.20)'
        : '0 2px 8px rgba(15,23,42,.08)',
      flexShrink: 0,
      /* Horizontal spacing lives here — NOT on the column wrapper —
         so ChildHat's border-top spans the full column width
         (card + margins) and the horizontal bar is never broken.  */
      margin: node.isRoot ? 0 : `0 ${H_GAP / 2}px`,
    }}>
      {/* Accent bar */}
      <div style={{ height: 4, background: t.bar }} />

      <div style={{ padding: '12px 12px 10px' }}>
        {/* Avatar + name row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: t.av, color: t.tx,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, flexShrink: 0, letterSpacing: 0.5,
          }}>
            {initials(node.name)}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 12.5, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {node.name || '—'}
            </p>
            {node.primaryRole && (
              <p style={{ margin: '1px 0 0', fontSize: 10.5, color: t.tx, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {node.primaryRole}{node.extraRoles > 0 ? ` +${node.extraRoles}` : ''}
              </p>
            )}
          </div>

          {node.level != null && (
            <span style={{ flexShrink: 0, background: '#f1f5f9', color: '#64748b', fontSize: 9.5, fontWeight: 700, padding: '2px 5px', borderRadius: 5 }}>
              L{node.level}
            </span>
          )}
        </div>

        {/* Email */}
        {node.email && (
          <p style={{ margin: '7px 0 0', fontSize: 10.5, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.email}
          </p>
        )}

        {/* Tags */}
        {node.entityType && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 7 }}>
            <span style={{ background: '#f8fafc', color: '#64748b', fontSize: 9.5, fontWeight: 500, padding: '2px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
              {node.entityType}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   STEP 4 · CUSTOM CSS TREE RENDERER
   Draws connector lines with pure CSS borders — no library.

   Each child column has a "hat" made of three divs:
     ┌──left half──┬─ center (vertical bar) ─┬──right half──┐
     │  border-top │       solid fill         │  border-top  │
     └─────────────┴──────────────────────────┴──────────────┘
   First child  → left half has NO border-top  (open left)
   Last child   → right half has NO border-top (open right)
   Middle child → both halves have border-top
   Only child   → neither half has border-top  (straight down)

   This produces perfectly connected ┬ junctions automatically.
───────────────────────────────────────────────────────────── */
function ChildHat({ isFirst, isLast }) {
  const isOnly = isFirst && isLast;
  return (
    <div style={{ display: 'flex', width: '100%', height: V_H }}>
      {/* Left half */}
      <div style={{
        flex: 1,
        borderTop: (!isOnly && !isFirst) ? `${LINE_W}px solid ${LINE}` : 'none',
      }} />
      {/* Vertical centre line */}
      <div style={{ width: LINE_W, background: LINE }} />
      {/* Right half */}
      <div style={{
        flex: 1,
        borderTop: (!isOnly && !isLast) ? `${LINE_W}px solid ${LINE}` : 'none',
      }} />
    </div>
  );
}

function OrgTreeNode({ node }) {
  const children = node.children ?? [];

  return (
    /* Column: card → vertical drop → children row */
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <OrgCard node={node} />

      {children.length > 0 && (
        <>
          {/* Vertical line from bottom of card down to horizontal bar */}
          <div style={{ width: LINE_W, height: V_H, background: LINE }} />

          {/* Children row — each wrapped in its own column */}
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {children.map((child, i) => (
              <div
                key={child.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  /* No horizontal padding here — spacing is on the card,
                     so ChildHat's border-top spans seamlessly. */
                }}
              >
                {/* "Hat" connector — draws the horizontal bar + drop */}
                <ChildHat isFirst={i === 0} isLast={i === children.length - 1} />
                {/* Recurse */}
                <OrgTreeNode node={child} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   STEP 5 · MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function HierarchyView() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tree, setTree]       = useState(null);
  const [error, setError]     = useState(null);
  const [zoom, setZoom]       = useState(ZOOM_DEFAULT);

  const zoomIn = () => {
    setZoom((current) => Math.min(ZOOM_MAX, Math.round((current + ZOOM_STEP) * 10) / 10));
  };

  const zoomOut = () => {
    setZoom((current) => Math.max(ZOOM_MIN, Math.round((current - ZOOM_STEP) * 10) / 10));
  };

  const zoomReset = () => setZoom(ZOOM_DEFAULT);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await rbacService.getInternalOrg();
        if (result.success) {
          setTree(normaliseOrgData(result.data, user));
        } else {
          setError(result.message || 'Failed to load org chart');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error loading organization hierarchy');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, gap: 14 }}>
        <Spin size="large" />
        <p style={{ color: '#64748b', margin: 0, fontSize: 14 }}>Loading organization hierarchy…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ border: '1px solid #fecaca', background: '#fff5f5', borderRadius: 12, padding: 24, textAlign: 'center' }}>
        <p style={{ color: '#b91c1c', fontWeight: 600, margin: 0 }}>{error}</p>
        <p style={{ color: '#ef4444', fontSize: 13, marginTop: 6 }}>Please refresh or contact support.</p>
      </div>
    );
  }

  if (!tree || tree.children.length === 0) {
    return (
      <div style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 12, padding: 48, textAlign: 'center' }}>
        <p style={{ color: '#475569', fontWeight: 600, margin: 0 }}>No hierarchy data available</p>
        <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>You have no direct reports or the org chart is empty.</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, padding: '0 4px' }}>
        <Space size="small" align="center">
          <span style={{ fontSize: 12, color: '#64748b', minWidth: 44, textAlign: 'center', fontWeight: 600 }}>
            {Math.round(zoom * 100)}%
          </span>
          <Tooltip title="Zoom out">
            <Button
              icon={<ZoomOutOutlined />}
              size="small"
              onClick={zoomOut}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Zoom out"
            />
          </Tooltip>
          <Tooltip title="Reset zoom">
            <Button
              icon={<CompressOutlined />}
              size="small"
              onClick={zoomReset}
              disabled={zoom === ZOOM_DEFAULT}
              aria-label="Reset zoom"
            />
          </Tooltip>
          <Tooltip title="Zoom in">
            <Button
              icon={<ZoomInOutlined />}
              size="small"
              onClick={zoomIn}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Zoom in"
            />
          </Tooltip>
        </Space>
      </div>

      <div
        style={{
          width: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          maxHeight: 'calc(100vh - 280px)',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          background: '#fafafa',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            justifyContent: 'center',
            minWidth: '100%',
            padding: '32px 24px 48px',
            boxSizing: 'border-box',
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
          }}
        >
          <OrgTreeNode node={tree} />
        </div>
      </div>
    </div>
  );
}
